import React from 'react';
// import { components, type InputProps } from "react-select";
import { useToggle } from '@react-hookz/web';
import classNames from 'classnames';
import { useQuery, gql } from 'modules/GraphQL';
import AsyncSelect from 'react-select/async';
import AsyncSelectCreatable from 'react-select/async-creatable';

export const styles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  input: (provided: any) => ({
    ...provided,
    border: 'none',
    boxShadow: 'none',
  }),
  control: (provided: any) => ({
    ...provided,
    '&:hover': {
      ...provided['&:hover'],
      borderRadius: 0,
    },
    minWidth: 200,
    borderRadius: 0,
    border: 'none',
  }),
};

type OnChangeSingle<T> = (data: T) => any;
type OnChangeMulti<T> = (data: T[]) => any;

type OnCreateSingle<T> = (data: T & { __isNew__: true }) => any;
type OnCreateMulti<T> = (data: (T & { __isNew__: true })[]) => any;

type ReactAsyncSelectProps<T> = React.ComponentProps<typeof AsyncSelect<T>>;

interface CustomAsyncAutocompleteProps<T> {
  components?: any;
  defaultValue: any;
  onChange: OnChangeMulti<T> | OnChangeSingle<T>;
  labelField: keyof T | ((data: T) => any);
  valueField?: keyof T | ((data: T) => any);
  isClearable?: boolean;
  isMulti?: boolean;
  closeMenuOnSelect?: boolean;
  cacheOptions?: boolean;
  queryFn?: any;
  trpc?: {
    query: (variables: any) => { data: any; isLoading: boolean; refetch: (variables?: any) => Promise<any> };
    path: string;
  };
  queryParams?: any;
  noOptionsMessage?: React.ComponentProps<typeof AsyncSelect>['noOptionsMessage'];
  className?: string;
  onCreate?: OnCreateMulti<T> | OnCreateSingle<T>;
}
export type AsyncAutocompleteProps<T> = CustomAsyncAutocompleteProps<T> & Omit<ReactAsyncSelectProps<T>, 'isMulti' | 'labelField'>;

const AsyncAutocomplete = <T,>({
  valueField = '_id' as keyof T,
  labelField,
  isClearable = true,
  placeholder,
  queryFn,
  trpc,
  queryParams,
  defaultValue,
  value: unusedValue,
  isMulti = false,
  onChange,
  onCreate,
  className,
  noOptionsMessage,
  ...props
}: AsyncAutocompleteProps<T>) => {
  const [q, setQ] = React.useState('');
  const [value, setValue] = React.useState<any>(null);
  const [values, setValues] = React.useState<T[]>([]);
  const [asyncFirstCallLoaded, toggleAsyncFirstCallLoaded] = useToggle(false);

  const getValueField: ReactAsyncSelectProps<T>['getOptionValue'] = React.useCallback(
    (option: T) => {
      if (typeof option === 'string' && Array.isArray(value)) {
        option = value.find(({ id }) => id === option);
      }

      return typeof valueField === 'function' ? valueField(option) : !option ? null : typeof valueField === 'string' ? option[valueField] : option;
    },
    [valueField, value]
  );

  const getLabelField: ReactAsyncSelectProps<T>['getOptionLabel'] = React.useCallback(
    (option: T) => {
      if (typeof option === 'string' && Array.isArray(value)) {
        option = value.find(({ id }) => id === option);
      }
      return typeof labelField === 'function' ? labelField(option) : !option ? null : typeof labelField === 'string' ? option[labelField] : option;
    },
    [labelField, value]
  );

  // GraphQL or tRPC logic
  const graphqlResult = useQuery(
    queryFn ||
      gql`
        query {
          nonExistentField
        }
      `,
    {
      variables: q ? { q, ...queryParams } : {},
      skip: !queryFn,
    }
  );

  const { loading: graphqlLoading, refetch } = queryFn ? graphqlResult : { loading: false, refetch: null };

  const trpcQuery = trpc?.query ? trpc.query({ search: q, ...queryParams }) : null;
  const loading = graphqlLoading || trpcQuery?.isLoading || false;

  const loadOptions = React.useCallback(
    async (searchTerm: string) => {
      setQ(searchTerm);

      if (queryFn && refetch) {
        // GraphQL path
        const result = await refetch({ q: searchTerm });
        const items = [...result.data.items.items];
        return items || [];
      } else if (trpc && trpcQuery) {
        // tRPC path
        const result = await trpcQuery.refetch({ search: searchTerm, ...queryParams });
        return result.data?.items || [];
      }

      return [];
    },
    [refetch, trpcQuery, queryFn, trpc, queryParams]
  );

  React.useEffect(() => {
    if (!defaultValue) {
      return;
    }
    if (Array.isArray(defaultValue) && defaultValue.length === 0) {
      return;
    }
    if (asyncFirstCallLoaded) {
      return;
    }

    const loadDefaultOptions = async () => {
      let result: any, items: T[];

      if (queryFn && refetch) {
        // GraphQL path
        result = await refetch({ q: '' });
        items = [...result.data.items.items];
      } else if (trpc && trpcQuery) {
        // tRPC path
        result = await trpcQuery.refetch({ search: '', ...queryParams });
        items = result.data?.items || [];
      } else {
        return;
      }

      if (Array.isArray(defaultValue)) {
        const defaultValues: T[] = await Promise.all(
          defaultValue.map(async (defaultVal) => {
            const existsInResult = items.find((item: any) => item._id === defaultVal);

            if (!existsInResult) {
              if (queryFn && refetch) {
                const res = await refetch({ q: defaultVal });
                return res.data?.items?.items[0];
              } else if (trpc && trpcQuery) {
                const res = await trpcQuery.refetch({ search: defaultVal, ...queryParams });
                return res.data?.items?.[0];
              }
            }
            return existsInResult;
          })
        );
        setValues(defaultValues);
      } else {
        const defaultValueId = defaultValue?.id || defaultValue;
        if (defaultValueId && !asyncFirstCallLoaded) {
          const existsInResult = items.find(({ _id }: any) => _id === defaultValueId);
          if (!existsInResult) {
            if (queryFn && refetch) {
              const defaultRes = await refetch({ q: defaultValueId });
              const defaultItem = defaultRes.data?.items?.items[0];
              setValue(defaultItem);
            } else if (trpc && trpcQuery) {
              const defaultRes = await trpcQuery.refetch({ search: defaultValueId, ...queryParams });
              const defaultItem = defaultRes.data?.items?.[0];
              setValue(defaultItem);
            }
          } else {
            setValue(existsInResult);
          }
        }
      }
    };

    toggleAsyncFirstCallLoaded();
    loadDefaultOptions();
  }, [defaultValue, asyncFirstCallLoaded, refetch, trpcQuery, queryFn, trpc, queryParams, toggleAsyncFirstCallLoaded]);

  const onChangeSingle: ReactAsyncSelectProps<T>['onChange'] = (newValue) => {
    if (onCreate && (newValue as any)?.__isNew__) {
      return (onCreate as OnCreateSingle<T>)(newValue as T & { __isNew__: true });
    }
    setValue(newValue);
    (onChange as OnChangeSingle<T>)(newValue as T);
  };

  const onChangeMulti: ReactAsyncSelectProps<T>['onChange'] = (newValue) => {
    const newValues = (newValue || []) as T[];
    if (onCreate && ((newValue as any)?.[0] as any)?.__isNew__) {
      return (onCreate as OnCreateMulti<T>)(newValue as (T & { __isNew__: true })[]);
    }
    setValues(newValues);
    (onChange as OnChangeMulti<T>)(newValues);
  };

  const AsyncSelectComponent = onCreate ? AsyncSelectCreatable : AsyncSelect;

  return (
    <AsyncSelectComponent
      cacheOptions
      defaultOptions
      loadOptions={loadOptions}
      styles={styles}
      isLoading={loading}
      isClearable={isClearable}
      isMulti={isMulti as any}
      getOptionLabel={getLabelField}
      getOptionValue={getValueField}
      onChange={(isMulti ? onChangeMulti : onChangeSingle) as any}
      value={isMulti ? values : value}
      placeholder={placeholder || 'Tape quelques lettres pour chercher'}
      noOptionsMessage={
        noOptionsMessage ||
        (({ inputValue }) =>
          inputValue === '' ? (
            'Tape quelques lettres pour chercher'
          ) : (
            <>
              Aucune option trouvée pour <strong>{inputValue}</strong>
            </>
          ))
      }
      menuPortalTarget={typeof document !== 'undefined' ? document.querySelector('body') : undefined}
      classNames={{
        control: (_state) => 'react-select__control',
        input: (_state) => 'react-select__input',
      }}
      className={classNames('react-select__async-autocomplete', className)}
      {...props}
    />
  );
};

export default AsyncAutocomplete;
